/** MC Show — presentation surface. States: LIVE_FEED (auto-scroll), WHO_WILL, CROSSWORD_CHAMPION_REVEAL.
 *  ?mode=audience strips all controls (projector-safe). Default = presenter (control bar on top). */
import { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { formatErr, fileUrl } from "../lib/api";
import { useEventSocket } from "../lib/ws";
import { useMotif } from "../contexts/MotifContext";
import { applyMotif } from "../lib/motif";
import { toast } from "sonner";
import { T, useI18n } from "../lib/i18n";
import {
  Trophy, Sparkles, Camera, Play, Pause, SkipForward, Square,
  ArrowLeft, MessageCircle, Gamepad2, ChevronDown, Heart,
} from "lucide-react";

const SPEEDS = { slow: 0.35, normal: 0.75, fast: 1.6 };

function publicName(full) {
  const parts = (full || "").trim().split(/\s+/);
  if (parts.length < 2) return full || "Guest";
  return `${parts[0]} ${parts[parts.length - 1][0].toUpperCase()}.`;
}

function fmtSecs(s) {
  if (s == null) return "—";
  const m = Math.floor(s / 60), r = s % 60;
  return `${m}:${String(r).padStart(2, "0")}`;
}

export default function MCShow() {
  useI18n();
  const { eventId } = useMotif();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const audience = params.get("mode") === "audience";

  const [presentation, setPresentation] = useState(null);
  const [session, setSession] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [feed, setFeed] = useState([]);

  const refresh = useCallback(async () => {
    try {
      const [p, s, f] = await Promise.all([
        api.get("/presentation"),
        api.get("/who-will/session"),
        api.get("/feed"),
      ]);
      setPresentation(p.data);
      setSession(s.data);
      setFeed(f.data);
      try {
        const { data: ev } = await api.get("/events/current");
        if (ev.motif) applyMotif(ev.motif);
      } catch (err) { console.debug("mcshow: motif fetch failed", err); }
      try {
        const { data: q } = await api.get("/who-will/questions");
        setQuestions(q);
      } catch (err) { console.debug("mcshow: questions fetch skipped (staff-only)", err); }
    } catch (err) { console.warn("mcshow: refresh failed", err); }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);
  useEventSocket(eventId, (msg) => {
    if (msg.type === "presentation_update") setPresentation(msg.payload);
    if (msg.type === "whowill_update") refresh();
    if (msg.type === "feed_new" || msg.type === "feed_update") refresh();
  });

  const state = presentation?.state || "LIVE_FEED";
  const whoWillLive = session && !["NOT_STARTED", "COMPLETE"].includes(session.state) && session.current_question;
  const showWhoWill = state === "WHO_WILL" && whoWillLive;
  const showChampion = state === "CROSSWORD_CHAMPION_REVEAL" && presentation?.payload;
  const showingFeed = !showWhoWill && !showChampion;

  return (
    <div className="min-h-screen bg-motif text-motif relative overflow-hidden" data-testid="mc-show">
      <div className="absolute top-0 left-0 right-0 h-1 bg-primary-motif opacity-80 z-10" />
      {!audience && !showingFeed && (
        <button
          onClick={() => navigate("/mc")}
          className="fixed top-3 left-3 z-40 btn-ghost text-[11px] px-2.5 py-1.5 flex items-center gap-1"
          data-testid="mc-show-back"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Dashboard
        </button>
      )}

      {showWhoWill ? (
        <WhoWillShow session={session} questions={questions} audience={audience} refresh={refresh} />
      ) : showChampion ? (
        <ChampionShow payload={presentation.payload} audience={audience} refresh={refresh} />
      ) : (
        <LiveFeedShow feed={feed} audience={audience} />
      )}
    </div>
  );
}

/* ---------------- WHO WILL ---------------- */
function WhoWillShow({ session, questions, audience, refresh }) {
  const q = session.current_question;
  const couple = session.couple || { aimee: "AIMEE", riyaz: "RIYAZ" };
  const t = session.tallies || { aimee: 0, riyaz: 0, total: 0 };
  const showLive = true; // live results always visible (Reveal step removed)
  const aimeePct = t.total ? (t.aimee / t.total) * 100 : 0;
  const riyazPct = t.total ? (t.riyaz / t.total) * 100 : 0;
  const qIdx = questions.findIndex((x) => x.id === q?.id);

  const call = async (path, verb) => {
    try {
      await api.post(path);
      toast.success(verb);
      refresh();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const stateLabel =
    session.state === "READY" ? T("GET READY") :
    session.state === "VOTING_OPEN" ? T("VOTING OPEN") :
    session.state === "VOTING_CLOSED" ? T("VOTING CLOSED") : session.state;

  return (
    <div className="min-h-screen flex flex-col" data-testid="mc-show-whowill">
      {!audience && (
        <div className="flex items-center gap-2 flex-wrap px-4 py-2.5 pl-32 glass border-b border-motif relative z-30" data-testid="mc-show-whowill-controls">
          <span className="text-[10px] tracking-luxe text-primary-motif">{stateLabel}</span>
          {qIdx >= 0 && <span className="text-[10px] text-motif-muted">Q{qIdx + 1}/{questions.length}</span>}
          <span className="flex-1" />
          {session.state === "READY" && (
            <button onClick={() => call("/who-will/session/open-voting", "Voting open")} className="btn-primary text-[11px] px-3 py-1.5 flex items-center gap-1" data-testid="mc-show-open-poll">
              <Play className="w-3 h-3" /> {T("Open Poll")}
            </button>
          )}
          {session.state === "VOTING_OPEN" && (
            <button onClick={() => call("/who-will/session/close-voting", "Voting closed")} className="btn-primary text-[11px] px-3 py-1.5 flex items-center gap-1" data-testid="mc-show-close-poll">
              <Pause className="w-3 h-3" /> Close Poll
            </button>
          )}
          {session.state === "VOTING_CLOSED" && (
            <button onClick={() => call("/who-will/session/next", "Next question")} className="btn-ghost text-[11px] px-3 py-1.5 flex items-center gap-1" data-testid="mc-show-next">
              <SkipForward className="w-3 h-3" /> Next
            </button>
          )}
          <button onClick={() => call("/who-will/session/end", "Game ended")} className="btn-ghost text-[11px] px-3 py-1.5 flex items-center gap-1" data-testid="mc-show-end">
            <Square className="w-3 h-3" /> End Game
          </button>
        </div>
      )}

      <div className="flex-1 flex flex-col items-center justify-center text-center px-10 py-8">
        <div className="flex items-center gap-2 text-sm tracking-luxe text-primary-motif mb-4">
          <Sparkles className="w-4 h-4" /> who will · {stateLabel.toLowerCase()}
        </div>
        <div className="font-display leading-[1.1] mb-10" style={{ fontSize: "clamp(2rem, 5vw, 5rem)" }} data-testid="mc-show-question">
          {q?.text}
        </div>

        <div className="flex items-center gap-10 flex-wrap justify-center">
          <div
            className="w-52 h-52 rounded-full flex-shrink-0"
            style={{
              background: showLive && t.total
                ? `conic-gradient(var(--motif-primary) 0 ${aimeePct}%, #6b7280 ${aimeePct}% 100%)`
                : "conic-gradient(#e5e7eb 0 100%)",
            }}
            aria-label="Vote breakdown chart"
          >
            <div className="w-full h-full flex items-center justify-center">
              <div className="w-36 h-36 rounded-full bg-motif flex items-center justify-center">
                <div className="text-center">
                  <div className="text-[10px] tracking-luxe text-motif-muted">{T("TOTAL")}</div>
                  <div className="font-display text-4xl text-motif tabular-nums" data-testid="mc-show-total-votes">{t.total}</div>
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 min-w-[280px]">
            {[["A", couple.aimee, t.aimee, aimeePct, "mc-show-aimee"], ["B", couple.riyaz, t.riyaz, riyazPct, "mc-show-riyaz"]].map(([k, name, count, pct, tid]) => (
              <div key={k} className="p-5 rounded-2xl bg-accent-motif flex items-center justify-between gap-8" data-testid={tid}>
                <div className="text-left">
                  <div className="text-xs tracking-luxe text-motif-muted">{k}</div>
                  <div className="font-display text-3xl">{name}</div>
                </div>
                {showLive && (
                  <div className="text-right">
                    <div className="font-display text-3xl text-primary-motif tabular-nums">{Math.round(pct)}%</div>
                    <div className="text-sm text-motif-muted tabular-nums">{count} votes</div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}


/* ---------------- CROSSWORD CHAMPION ---------------- */
function ChampionShow({ payload, audience, refresh }) {
  const endReveal = async () => {
    try {
      await api.post("/mc/crossword/reveal/end");
      toast.success("Back to Live Feed");
      refresh();
    } catch (e) { toast.error(formatErr(e)); }
  };
  return (
    <div className="fixed inset-0 overflow-hidden spotlight-backdrop" data-testid="mc-show-champion">
      <div className="absolute inset-0 spotlight-glow" />
      {Array.from({ length: 26 }, (_, i) => (
        <span key={`c-${(i * 37 + 11) % 100}`} className="spotlight-confetti" style={{
          left: `${(i * 37 + 11) % 100}%`, width: 7 + (i % 3) * 5, height: (7 + (i % 3) * 5) * 0.45,
          animationDelay: `${(i % 8) * 0.5}s`, animationDuration: `${4.5 + (i % 5) * 0.9}s`,
          background: i % 3 !== 0 ? "var(--motif-primary, #c2a14d)" : "#f6efe1",
        }} />
      ))}
      {!audience && (
        <button onClick={endReveal}
          className="fixed top-3 right-3 z-40 text-[11px] px-3 py-1.5 rounded-full flex items-center gap-1"
          style={{ background: "rgba(246,239,225,0.14)", color: "#e3c96f" }}
          data-testid="mc-show-end-reveal">
          <Square className="w-3 h-3" /> End Reveal · Return to Live Feed
        </button>
      )}
      <div className="relative h-full flex flex-col items-center justify-center text-center px-8">
        <div className="spotlight-rise flex items-center gap-2 text-sm tracking-luxe" style={{ color: "#d9c489" }}>
          <Sparkles className="w-4 h-4" /> crossword champion
        </div>
        <div className="spotlight-pop mt-5">
          <Trophy className="w-16 h-16 mx-auto" style={{ color: "#e3c96f" }} />
        </div>
        <div className="spotlight-pop font-display gold-shimmer leading-tight mt-3" style={{ fontSize: "clamp(2.6rem, 8vw, 8rem)", animationDelay: "0.25s" }} data-testid="mc-show-champion-name">
          {payload.display_name}
        </div>
        {payload.table_name && (
          <div className="spotlight-rise font-display text-2xl mt-1" style={{ color: "#cbb26a", animationDelay: "0.5s" }}>
            Table {String(payload.table_name).replace(/^table\s*/i, "")}
          </div>
        )}
        <div className="spotlight-rise flex items-center gap-8 mt-8 flex-wrap justify-center" style={{ animationDelay: "0.7s" }}>
          <Stat label={T("FINAL SCORE")} value={(payload.final_score ?? 0).toLocaleString()} />
          <Stat label={T("CORRECT")} value={`${payload.correct_clue_count}/${payload.total_clue_count}`} />
          <Stat label={T("STATUS")} value={payload.submission_type === "COMPLETE" ? T("Complete") : T("Partial")} />
          <Stat label={T("TIME")} value={fmtSecs(payload.elapsed_seconds)} />
        </div>
        <div className="spotlight-rise font-display text-3xl mt-10" style={{ color: "#f0e3bb", animationDelay: "1s" }}>
          {T("Congratulations!")}
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div className="text-[10px] tracking-luxe" style={{ color: "#9d8b57" }}>{label}</div>
      <div className="font-display text-4xl tabular-nums" style={{ color: "#f0e3bb" }}>{value}</div>
    </div>
  );
}

/* ---------------- LIVE FEED (auto-scroll ping-pong) ---------------- */
function LiveFeedShow({ feed, audience }) {
  const navigate = useNavigate();
  const scrollRef = useRef(null);
  const dirRef = useRef(1);
  const pauseUntilRef = useRef(0);
  const [running, setRunning] = useState(true);
  const [speed, setSpeed] = useState("normal");
  const [gamesOpen, setGamesOpen] = useState(false);
  const [champBusy, setChampBusy] = useState(false);
  const runningRef = useRef(true);
  const speedRef = useRef("normal");
  runningRef.current = running;
  speedRef.current = speed;

  const posts = useMemo(() => feed.filter((p) => p.image_url || p.caption), [feed]);

  const startWhoWill = async () => {
    setGamesOpen(false);
    try {
      await api.post("/who-will/session/start", {});
      toast.success("Who Will is live");
    } catch (e) { toast.error(formatErr(e)); }
  };

  const revealChampion = async () => {
    setGamesOpen(false);
    if (champBusy) return;
    setChampBusy(true);
    try {
      const { data } = await api.get("/mc/crossword/leaderboard");
      if (!data.rows || data.rows.length === 0) {
        toast("No submitted crossword scores are available yet.");
        return;
      }
      await api.post("/mc/crossword/reveal/start", { guest_id: data.rows[0].guest_id });
      toast.success(`🏆 ${data.rows[0].guest_name} revealed`);
    } catch (e) { toast.error(formatErr(e)); } finally { setChampBusy(false); }
  };

  useEffect(() => {
    let raf;
    const step = () => {
      const el = scrollRef.current;
      if (el && runningRef.current && Date.now() > pauseUntilRef.current) {
        const max = el.scrollHeight - el.clientHeight;
        if (max > 4) {
          el.scrollTop += dirRef.current * SPEEDS[speedRef.current];
          if (el.scrollTop >= max - 1) { dirRef.current = -1; pauseUntilRef.current = Date.now() + 2500; }
          if (el.scrollTop <= 1) { dirRef.current = 1; pauseUntilRef.current = Date.now() + 2500; }
        }
      }
      raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, []);

  const holdOnInteract = () => { pauseUntilRef.current = Date.now() + 6000; };

  return (
    <div className="min-h-screen flex flex-col" data-testid="mc-show-livefeed">
      <header className="px-10 pt-5 pb-4">
        {!audience && (
          <div className="flex items-center justify-between gap-3">
            <button
              onClick={() => navigate("/mc")}
              className="btn-ghost text-[10px] px-2 py-1 flex items-center gap-1 flex-shrink-0"
              data-testid="mc-show-back"
            >
              <ArrowLeft className="w-3 h-3" /> {T("Dashboard")}
            </button>
            <div className="flex items-center gap-1.5" data-testid="mc-show-feed-controls">
              <button onClick={() => setRunning((r) => !r)} className="btn-ghost text-[10px] px-2 py-1 flex items-center gap-1" data-testid="mc-show-feed-pause">
                {running ? <Pause className="w-3 h-3" /> : <Play className="w-3 h-3" />} {running ? T("Pause") : T("Resume")}
              </button>
              {Object.keys(SPEEDS).map((s) => (
                <button key={s} onClick={() => setSpeed(s)}
                  className="text-[10px] px-2 py-1 rounded-full capitalize"
                  style={{
                    background: speed === s ? "var(--motif-primary)" : "var(--motif-accent)",
                    color: speed === s ? "var(--motif-primary-fg)" : "var(--motif-text-muted)",
                  }}
                  data-testid={`mc-show-feed-speed-${s}`}>{s}</button>
              ))}
            </div>
            <div className="relative flex-shrink-0">
              <button
                onClick={() => setGamesOpen((o) => !o)}
                className="btn-ghost text-[10px] px-2 py-1 flex items-center gap-1"
                data-testid="mc-show-games"
              >
                <Gamepad2 className="w-3 h-3" /> Games <ChevronDown className="w-3 h-3" />
              </button>
              {gamesOpen && (
                <>
                  <div className="fixed inset-0 z-30" onClick={() => setGamesOpen(false)} />
                  <div className="absolute right-0 top-full mt-1 z-40 card-elev p-1.5 min-w-[230px] space-y-1" data-testid="mc-show-games-menu">
                    <button onClick={startWhoWill}
                      className="w-full text-left text-xs px-3 py-2 rounded-lg hover:bg-accent-motif flex items-center gap-2 text-motif"
                      data-testid="mc-show-games-whowill">
                      <Play className="w-3.5 h-3.5 text-primary-motif" /> Start Who Will
                    </button>
                    <button onClick={revealChampion} disabled={champBusy}
                      className="w-full text-left text-xs px-3 py-2 rounded-lg hover:bg-accent-motif flex items-center gap-2 text-motif disabled:opacity-60"
                      data-testid="mc-show-games-champion">
                      <Trophy className="w-3.5 h-3.5 text-primary-motif" /> Reveal Crossword Champion
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
        <div className={`flex items-center justify-center gap-2 text-sm tracking-luxe text-primary-motif ${audience ? "" : "mt-4"}`} data-testid="mc-show-feed-title">
          <Camera className="w-4 h-4" /> live feed
        </div>
      </header>

      <div
        ref={scrollRef}
        onMouseMove={holdOnInteract}
        onTouchStart={holdOnInteract}
        onWheel={holdOnInteract}
        className="flex-1 overflow-y-auto no-scrollbar px-10 pb-12"
        style={{ maxHeight: "calc(100vh - 150px)" }}
        data-testid="mc-show-feed-scroll"
      >
        {posts.length === 0 ? (
          <div className="h-[60vh] flex items-center justify-center text-motif-muted font-display text-3xl text-center" data-testid="mc-show-feed-empty">
            Guest photos and messages will appear here.
          </div>
        ) : (
          <div className="columns-1 md:columns-2 xl:columns-3 gap-6 max-w-6xl mx-auto">
            {posts.map((p) => (
              <div key={p.id} className="card-elev p-4 mb-6 break-inside-avoid" data-testid={`mc-show-feed-post-${p.id}`}>
                {p.image_url && (
                  <img src={fileUrl(p.image_url)} alt="" className="w-full rounded-xl object-cover mb-3" loading="lazy" />
                )}
                {p.caption && (
                  <div className="font-display text-xl text-motif leading-snug mb-1">{p.caption}</div>
                )}
                <div className="flex items-center justify-between gap-2">
                  <div className="text-sm text-primary-motif tracking-wide">{publicName(p.author_name)}</div>
                  {(p.likes || []).length > 0 && (
                    <div className="flex items-center gap-1 text-sm" style={{ color: "#c2483f" }} data-testid={`mc-show-likes-${p.id}`}>
                      <Heart className="w-4 h-4" fill="#c2483f" />
                      <span className="tabular-nums">{p.likes.length}</span>
                    </div>
                  )}
                </div>
                <PostComments comments={p.comments} />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function PostComments({ comments }) {
  const list = comments || [];
  if (list.length === 0) return null;
  const shown = list.slice(-3); // most recent 3, chronological
  const extra = list.length - shown.length;
  return (
    <div className="mt-2 pt-2 space-y-1" style={{ borderTop: "1px solid var(--motif-border, rgba(0,0,0,0.08))" }}>
      {shown.map((c) => (
        <div key={c.id} className="flex items-start gap-1.5 text-sm leading-snug" data-testid={`mc-show-comment-${c.id}`}>
          <MessageCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-primary-motif" />
          <div className="min-w-0 break-words">
            <span className="text-primary-motif">{publicName(c.author)}:</span>{" "}
            <span className="text-motif">{c.text}</span>
          </div>
        </div>
      ))}
      {extra > 0 && (
        <div className="text-[11px] text-motif-muted italic pl-5" data-testid="mc-show-comments-more">
          + {extra} more {extra === 1 ? "comment" : "comments"}
        </div>
      )}
    </div>
  );
}
